import React, { useState } from 'react';
import { FiCopy, FiCheck } from 'react-icons/fi';
import { getTemplateHeaders, essentialFieldsHP, essentialFieldsSND, essentialFieldsCTL } from './constants';
import { getNextFieldForNavigation } from './utils';
import FieldRenderer from './FieldRenderer';

const QuickStatsForm = ({
  activeMode,
  structuredData,
  updateCell,
  activePlayers,
  playerImages,
  matchTypes,
  vsTeams,
  inputRefs,
  showAllColumns,
  setShowAllColumns,
  viewToggleControl = null,
  importControl = null
}) => {
  const [copiedValue, setCopiedValue] = useState(null);

  if (!activeMode || !structuredData.length) return null;

  const headers = getTemplateHeaders(activeMode);
  const baseEssentialFields = activeMode === 'HP' ? essentialFieldsHP : activeMode === 'SND' ? essentialFieldsSND : essentialFieldsCTL;
  const essentialColumns = headers.filter(h => 
    baseEssentialFields.includes(h) || 
    ['Map', 'Operator', 'MatchType'].includes(h)
  );
  const advancedColumns = headers.filter(h => 
    !essentialColumns.includes(h) && 
    !['Team', 'Region', 'VS', 'Date', 'ModeID'].includes(h) &&
    !(h === 'Operator' && activeMode === 'SND')
  );

  const displayColumns = showAllColumns 
    ? [...essentialColumns, ...advancedColumns]
    : essentialColumns;

  const handleKeyDown = (e, fieldKey, row) => {
    const isTab = e.key === 'Tab' && !e.shiftKey;
    const isEnter = e.key === 'Enter';
    const isArrowDown = e.key === 'ArrowDown';

    if (isArrowDown) {
      e.preventDefault();
      const currentIndex = structuredData.findIndex(r => r._rowId === row._rowId);
      const nextRow = structuredData[currentIndex + 1];
      if (nextRow) {
        const refKey = `${nextRow._rowId}-${fieldKey}`;
        inputRefs.current[refKey]?.focus();
        return;
      }
    }

    if (!isTab && !isEnter) return;

    const currentIndex = structuredData.findIndex(r => r._rowId === row._rowId);
    const isLastRow = currentIndex === structuredData.length - 1;
    const refKey = (rid, col) => `${rid}-${col}`;

    // Advanced fields navigation (Hardpoint): TotalDamage → CaptureKills → DistanceMoved → next row TotalDamage...
    if (activeMode === 'HP' && showAllColumns && isTab) {
      if (fieldKey === 'TotalDamage') {
        e.preventDefault();
        inputRefs.current[refKey(row._rowId, 'CaptureKills')]?.focus();
        return;
      }

      if (fieldKey === 'CaptureKills') {
        e.preventDefault();
        inputRefs.current[refKey(row._rowId, 'DistanceMoved')]?.focus();
        return;
      }

      if (fieldKey === 'DistanceMoved') {
        e.preventDefault();
        if (isLastRow) {
          const firstRow = structuredData[0];
          if (firstRow) {
            inputRefs.current[refKey(firstRow._rowId, 'OperatorValue')]?.focus();
          }
        } else {
          const nextRow = structuredData[currentIndex + 1];
          if (nextRow) {
            inputRefs.current[refKey(nextRow._rowId, 'TotalDamage')]?.focus();
          }
        }
        return;
      }
    }

    // Advanced fields navigation: ADR → FirstBloods → Clutches1v1 → next row ADR...
    if (showAllColumns && isTab) {
      if (fieldKey === 'ADR') {
        e.preventDefault();
        inputRefs.current[refKey(row._rowId, 'FirstBloods')]?.focus();
        return;
      }

      if (fieldKey === 'FirstBloods') {
        e.preventDefault();
        inputRefs.current[refKey(row._rowId, 'Clutches1v1')]?.focus();
        return;
      }

      if (fieldKey === 'Clutches1v1') {
        e.preventDefault();
        if (isLastRow) {
          const firstRow = structuredData[0];
          if (firstRow) {
            inputRefs.current[refKey(firstRow._rowId, 'ADR')]?.focus();
          }
        } else {
          const nextRow = structuredData[currentIndex + 1];
          if (nextRow) {
            inputRefs.current[refKey(nextRow._rowId, 'ADR')]?.focus();
          }
        }
        return;
      }
    }

    const nextField = getNextFieldForNavigation(activeMode, fieldKey, isEnter, showAllColumns);
    
    if (nextField) {
      const nextRow = structuredData[currentIndex + 1];
      if (nextRow) {
        e.preventDefault();
        inputRefs.current[refKey(nextRow._rowId, nextField)]?.focus();
      }
    }
  };

  const quickFillField = (fieldKey) => {
    if (!structuredData[0]) return;
    const value = structuredData[0][fieldKey];
    if (value) {
      structuredData.forEach((row, index) => {
        if (index > 0) {
          updateCell(row._rowId, fieldKey, value);
        }
      });
      setCopiedValue(fieldKey);
      setTimeout(() => setCopiedValue(null), 2000);
    }
  };

  const quickFillAll = () => {
    const fieldsToFill = ['Map', 'Operator', 'MatchType', 'Region', 'VS'];
    fieldsToFill.forEach(field => {
      if (structuredData[0]?.[field]) {
        structuredData.forEach((row, index) => {
          if (index > 0) {
            updateCell(row._rowId, field, structuredData[0][field]);
          }
        });
      }
    });
    setCopiedValue('all');
    setTimeout(() => setCopiedValue(null), 2000);
  };

  return (
    <div className="quick-stats-form">
      <div className="quick-actions-bar">
        <div className="quick-actions-left">
        <button
          type="button"
          onClick={quickFillAll}
          className="quick-fill-btn"
          title="Copy first row values to all rows"
        >
          {copiedValue === 'all' ? <FiCheck /> : <FiCopy />} Quick Fill All
        </button>
        <button
          type="button"
          onClick={() => setShowAllColumns(!showAllColumns)}
          className="toggle-columns-btn"
        >
          {showAllColumns ? 'Show Essential' : 'Show All Fields'}
        </button>
        </div>
        {viewToggleControl && (
          <div className="quick-actions-right">
            {importControl}
            {viewToggleControl}
          </div>
        )}
      </div>

      <div className="compact-table-wrapper">
        <table className="quick-stats-table">
          <thead>
            <tr>
              {displayColumns.map((header, idx) => {
                const isEssential = essentialColumns.includes(header);
                const firstRowValue = structuredData[0]?.[header];
                const canQuickFill = ['Map', 'Operator', 'MatchType', 'Region', 'VS'].includes(header);
                
                return (
                  <th key={idx} className={isEssential ? 'essential-column' : ''}>
                    <div className="header-cell-content">
                      <span>{header}</span>
                      {canQuickFill && firstRowValue && (
                        <button
                          type="button"
                          onClick={() => quickFillField(header)}
                          className="quick-fill-icon"
                          title={`Copy ${header} to all rows`}
                        >
                          {copiedValue === header ? <FiCheck /> : <FiCopy />}
                        </button>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {structuredData.map((row, rowIdx) => (
              <tr key={row._rowId} className={rowIdx === 0 ? 'first-row' : ''}>
                {displayColumns.map((fieldKey, colIdx) => {
                  if (fieldKey === 'Operator' && activeMode === 'SND') return null;
                  
                  return (
                    <td key={colIdx} className={rowIdx === 0 && colIdx === 0 ? 'first-column' : ''}>
                      <FieldRenderer
                        fieldKey={fieldKey}
                        row={row}
                        value={row[fieldKey] || ''}
                        activeMode={activeMode}
                        activePlayers={activePlayers}
                        playerImages={playerImages}
                        matchTypes={matchTypes}
                        vsTeams={vsTeams}
                        structuredData={structuredData}
                        updateCell={updateCell}
                        inputRefs={inputRefs}
                        onKeyDown={(e) => handleKeyDown(e, fieldKey, row)}
                        showAllColumns={showAllColumns}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default QuickStatsForm;

